import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import type { MapMode } from '../../services/trip-feature-manager.js'

const MODE_CONFIG: Record<MapMode, { icon: string; label: string }> = {
  map: { icon: 'map', label: 'Map' },
  tracking: { icon: 'explore', label: 'Tracking' },
  draw: { icon: 'edit', label: 'Draw' },
  select: { icon: 'photo_size_select_small', label: 'Select' },
  measure: { icon: 'straighten', label: 'Measure' },
}

const BASE_MODES: MapMode[] = ['map', 'draw', 'select', 'measure']

const FAB_DIAMETER = 53
const FAB_RADIUS = FAB_DIAMETER / 2
const INNER_RADIUS = FAB_RADIUS + 16
const ARC_START = 165
const ARC_SWEEP = 120
const RING_THICKNESS = 48
const OUTER_RADIUS = INNER_RADIUS + RING_THICKNESS
const RING_DIAMETER = OUTER_RADIUS * 2

function polarPt(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

@customElement('mode-fab')
export class ModeFab extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
      width: ${FAB_DIAMETER}px;
      height: ${FAB_DIAMETER}px;
      touch-action: none;
    }

    .fab-button {
      width: ${FAB_DIAMETER}px;
      height: ${FAB_DIAMETER}px;
      border-radius: 50%;
      border: none;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 2;
      position: relative;
      transition: transform 0.15s ease;
      touch-action: none;
    }

    .fab-button:active {
      transform: scale(0.95);
    }

    .fab-button .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 24px;
    }

    .ring-container {
      position: absolute;
      width: ${RING_DIAMETER}px;
      height: ${RING_DIAMETER}px;
      right: ${FAB_RADIUS - OUTER_RADIUS}px;
      bottom: ${FAB_RADIUS - OUTER_RADIUS}px;
      clip-path: polygon(0% 0%, 100% 0%, 100% 50%, 50% 50%, 50% 100%, 0% 100%);
      pointer-events: none;
      opacity: 0;
      transform: scale(0.6);
      transform-origin: ${OUTER_RADIUS}px ${OUTER_RADIUS}px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 1;
    }

    .ring-container.visible {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    .ring {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: var(--cairn-surface-color, #ffffff);
      border-radius: 50%;
      cursor: pointer;
    }

    .ring-icon {
      position: absolute;
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
      color: var(--cairn-text-secondary-color, #64748b);
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .ring-icon:hover,
    .ring-icon.hovered {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .ring-icon:active {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
    }

    .backdrop.visible {
      display: block;
    }

    .layers-button {
      position: absolute;
      bottom: calc(100% + 8px);
      right: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }

    .layers-button:active {
      transform: scale(0.95);
    }

    .layers-button .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
    }
  `

  @property({ type: String })
  activeMode: MapMode = 'map'

  @property({ type: Boolean })
  locationPermissionGranted = false

  @state()
  private isOpen = false

  @state()
  private hoveredMode: MapMode | null = null

  private isDragging = false
  private dragStartX = 0
  private dragStartY = 0
  private wasOpenOnPointerDown = false
  private readonly MIN_DRAG_DIST = 30

  private get availableModes(): MapMode[] {
    if (this.locationPermissionGranted) {
      return [...BASE_MODES.slice(0, 1), 'tracking', ...BASE_MODES.slice(1)]
    }
    return BASE_MODES
  }

  private get otherModes(): MapMode[] {
    return this.availableModes.filter(m => m !== this.activeMode)
  }

  private closeMenu(): void {
    this.isOpen = false
    this.hoveredMode = null
  }

  private selectMode(mode: MapMode): void {
    this.isOpen = false
    this.hoveredMode = null
    this.dispatchEvent(
      new CustomEvent('mode-changed', {
        detail: { mode },
        bubbles: true,
      })
    )
  }

  private getModeForAngle(clientX: number, clientY: number): MapMode | null {
    const dx = clientX - this.dragStartX
    const dy = clientY - this.dragStartY
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist < this.MIN_DRAG_DIST) {
      return null
    }

    const angle = (Math.atan2(dy, dx) * 180) / Math.PI
    const normalizedAngle = angle < 0 ? angle + 360 : angle

    const arcEnd = ARC_START + ARC_SWEEP
    if (normalizedAngle < ARC_START - 15 || normalizedAngle > arcEnd + 15) {
      return null
    }

    const others = this.otherModes
    const total = others.length
    const spacing = ARC_SWEEP / (total - 1)
    const idx = Math.round(
      (Math.max(ARC_START, Math.min(arcEnd, normalizedAngle)) - ARC_START) / spacing
    )
    const clamped = Math.max(0, Math.min(total - 1, idx))
    return others[clamped]
  }

  private handleFabPointerDown(e: PointerEvent): void {
    e.preventDefault()
    this.isDragging = true
    this.wasOpenOnPointerDown = this.isOpen
    const fabBtn = this.renderRoot.querySelector('.fab-button') as HTMLElement
    const fabRect = fabBtn.getBoundingClientRect()
    this.dragStartX = fabRect.left + fabRect.width / 2
    this.dragStartY = fabRect.top + fabRect.height / 2
    this.isOpen = true
    this.hoveredMode = null
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  private handleFabPointerMove(e: PointerEvent): void {
    if (!this.isDragging) {
      return
    }
    this.hoveredMode = this.getModeForAngle(e.clientX, e.clientY)
  }

  private handleFabPointerUp(e: PointerEvent): void {
    if (!this.isDragging) {
      return
    }
    this.isDragging = false
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)

    const mode = this.getModeForAngle(e.clientX, e.clientY)
    if (mode) {
      this.selectMode(mode)
    } else if (this.wasOpenOnPointerDown) {
      this.closeMenu()
    }
  }

  private handleRingClick(e: MouseEvent): void {
    const fabBtn = this.renderRoot.querySelector('.fab-button')
    if (!fabBtn) {
      return
    }
    const fabRect = fabBtn.getBoundingClientRect()
    const fabCenterX = fabRect.left + fabRect.width / 2
    const fabCenterY = fabRect.top + fabRect.height / 2
    const dx = e.clientX - fabCenterX
    const dy = e.clientY - fabCenterY
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI
    const normalizedAngle = angle < 0 ? angle + 360 : angle
    const arcEnd = ARC_START + ARC_SWEEP
    if (normalizedAngle < ARC_START || normalizedAngle > arcEnd) {
      return
    }
    const others = this.otherModes
    const total = others.length
    const spacing = ARC_SWEEP / (total - 1)
    const idx = Math.round((normalizedAngle - ARC_START) / spacing)
    const clamped = Math.max(0, Math.min(total - 1, idx))
    this.selectMode(others[clamped])
  }

  private handleLayersClick(): void {
    this.dispatchEvent(new CustomEvent('toggle-layers', { bubbles: true }))
  }

  override render(): unknown {
    const config = MODE_CONFIG[this.activeMode]
    const others = this.otherModes
    const total = others.length

    const cx = OUTER_RADIUS
    const cy = OUTER_RADIUS
    const midR = (INNER_RADIUS + OUTER_RADIUS) / 2
    const icons = others.map((mode, i) => {
      const midDeg = ARC_START + (ARC_SWEEP / (total - 1)) * i
      const pos = polarPt(cx, cy, midR, midDeg)
      const mc = MODE_CONFIG[mode]
      return { mode, icon: mc.icon, label: mc.label, pos }
    })

    return html`
      <div class="backdrop ${this.isOpen ? 'visible' : ''}" @click=${this.closeMenu}></div>

      <button class="layers-button" @click=${this.handleLayersClick} title="Change map layers">
        <span class="material-icons">layers</span>
      </button>

      <div class="ring-container ${this.isOpen ? 'visible' : ''}">
        <div
          class="ring"
          @click=${this.handleRingClick}
          style="mask: radial-gradient(circle at center, transparent ${INNER_RADIUS}px, black ${INNER_RADIUS}px);
                 -webkit-mask: radial-gradient(circle at center, transparent ${INNER_RADIUS}px, black ${INNER_RADIUS}px);"
        >
          ${icons.map(
            ic => html`
              <span
                class="ring-icon material-icons ${this.hoveredMode === ic.mode ? 'hovered' : ''}"
                style="left: ${ic.pos.x}px; top: ${ic.pos.y}px"
                title=${ic.label}
                @click=${(e: Event): void => {
                  e.stopPropagation()
                  this.selectMode(ic.mode)
                }}
                >${ic.icon}</span
              >
            `
          )}
        </div>
      </div>

      <button
        class="fab-button"
        @pointerdown=${this.handleFabPointerDown}
        @pointermove=${this.handleFabPointerMove}
        @pointerup=${this.handleFabPointerUp}
        title=${config.label}
      >
        <span class="material-icons">${config.icon}</span>
      </button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mode-fab': ModeFab
  }
}
